
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task } from "@/types/task";

interface TaskStats {
  active_tasks_count: number;
  overdue_tasks_count: number;
  nearest_due_date: string | null;
  priority_breakdown: Record<string, number>;
}

// Raw task type from database view
interface RawTaskFromView {
  actual_hours: number | null;
  assigned_employee_name: string | null;
  assigned_to: string | null;
  attachments: string[] | null;
  category_color: string | null;
  category_id: string | null;
  category_name: string | null;
  client_id: string | null;
  client_name: string | null;
  company_id: string | null;
  company_name: string | null;
  completed_at: string | null;
  created_at: string;
  created_by: string | null;
  description: string | null;
  due_date: string | null;
  estimated_hours: number | null;
  id: string;
  priority: string;
  start_date: string | null;
  status: string;
  supplier_id: string | null;
  supplier_name: string | null;
  tags: string[] | null;
  title: string;
  type: string;
  updated_at: string;
}

export const useOptimizedTasks = (userId?: string) => {
  const { data: rawTasks = [], isLoading, error } = useQuery({
    queryKey: ['active-tasks'],
    queryFn: async (): Promise<RawTaskFromView[]> => {
      const { data, error } = await supabase
        .from('active_tasks_view')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Convert raw tasks to proper Task type
  const tasks: Task[] = rawTasks.map(rawTask => ({
    ...rawTask,
    status: rawTask.status as Task['status'],
    priority: rawTask.priority as Task['priority'],
    // Map database view fields to Task interface
    task_categories: rawTask.category_name ? {
      name_ar: rawTask.category_name,
      color: rawTask.category_color || '#6366f1'
    } : undefined,
    clients: rawTask.client_name ? {
      name_ar: rawTask.client_name
    } : undefined,
    companies: rawTask.company_name ? {
      name_ar: rawTask.company_name
    } : undefined,
    suppliers: rawTask.supplier_name ? {
      name_ar: rawTask.supplier_name
    } : undefined,
    assigned_employee: rawTask.assigned_employee_name ? {
      name: rawTask.assigned_employee_name
    } : undefined,
  }));

  const { data: rawUserStats } = useQuery({
    queryKey: ['user-task-stats', userId],
    queryFn: async (): Promise<any> => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .rpc('get_user_task_stats', { user_uuid: userId });
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Convert raw stats to proper TaskStats type
  const userStats: TaskStats | null = rawUserStats ? {
    active_tasks_count: rawUserStats.active_tasks_count || 0,
    overdue_tasks_count: rawUserStats.overdue_tasks_count || 0,
    nearest_due_date: rawUserStats.nearest_due_date || null,
    priority_breakdown: rawUserStats.priority_breakdown || {},
  } : null;

  const getMyActiveTasks = () => {
    if (!userId || !tasks) return [];
    return tasks.filter(task => task.assigned_to === userId);
  };

  const getNearestDueDate = (): string | null => {
    if (userStats?.nearest_due_date) {
      return userStats.nearest_due_date;
    }
    
    const myTasks = getMyActiveTasks();
    if (myTasks.length === 0) return null;
    
    const now = Date.now();
    let nearestDate: string | null = null;
    let earliestTime = Infinity;
    
    for (const task of myTasks) {
      if (task.due_date) {
        const taskTime = new Date(task.due_date).getTime();
        if (taskTime > now && taskTime < earliestTime) {
          earliestTime = taskTime;
          nearestDate = task.due_date;
        }
      }
    }
    
    return nearestDate;
  };

  return {
    tasks,
    isLoading,
    error,
    userStats,
    myActiveTasks: getMyActiveTasks(),
    nearestDueDate: getNearestDueDate(),
    myTasksCount: userStats?.active_tasks_count || getMyActiveTasks().length,
  };
};
